import { Component, Input, OnInit } from '@angular/core';
import { PopupService } from '../../services/popup.service';
import { SchoolsPoiProperties } from '../../../menu/right-menu/layers-menu/education/education.service';
import { CrimeRateByYear } from '../../../menu/right-menu/layers-menu/points.service';
import { Feature } from 'geojson';
import {
  H3_RES5_LEVEL_LAYER,
  STATE_LEVEL_LAYER,
  LayerStoreService,
} from '../../services/layer-store.service';
import { SelectedCellService } from '../../services/selected-cell.service';
import { UserAccessService } from '../../../../user/access/user-access.service';
import {
  featuresToCompareToNationalPopup,
  LAND_AREA_FRACTION,
} from '../../../../shared/types/feature-data-type';
import { MapHttpService } from '../../services/map-http.service';
import { MapUrlService } from '../../services/map-url.service';
import {
  AsyncPipe,
  NgClass,
  NgIf,
  NgSwitch,
  NgSwitchCase,
} from '@angular/common';
import { LevelsPopupListComponent } from './layers-popup-list/levels-popup-list.component';
import { GroupedPopupListComponent } from './grouped-popup-list/grouped-popup-list.component';
import { SortPropertiesPipe } from './sort-properties.pipe';
import { SchoolsPopupListComponent } from './schools-popup-list/schools-popup-list.component';
import { OriPopupListComponent } from './ori-popup-list/ori-popup-list.component';
import { MatTooltip } from '@angular/material/tooltip';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../../../shared/directives/icon-button.directive';
import { ModalService } from '../../../../shared/services/modal.service';
import { MapModalData } from '../../../../user/map-redirect-modal/map-redirect-modal.component';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { salesForecastPath } from '../../../../sales-forecast/sales-forecast.routes-data';
import { ColorscaleService } from '../colorscale/colorscale.service';
import { GoogleAnalyticsService } from '../../../../user/google-analytics.service';
import { openLockedFeaturePrompt } from '../../../menu/right-menu/layers-menu/shared/feature-tooltip/inacessible-features-util';
import { HWW_REQUEST_FEATURES, spreadHwwScopes } from './hww-grade';
import { USER_CATEGORY_KEY } from '../../../user-guide/user-guide.service';

export const DEFAULT_POPUP = 'DEFAULT_POPUP';
export const SCHOOLS_POPUP = 'SCHOOLS_POPUP';
export const ORI_POPUP = 'ORI_POPUP';
export const HOVER_POPUP = 'HOVER_POPUP';

export type PopupData = {
  type:
    | typeof DEFAULT_POPUP
    | typeof SCHOOLS_POPUP
    | typeof ORI_POPUP
    | typeof HOVER_POPUP;
  properties: Feature['properties'] | SchoolsPoiProperties | CrimeRateByYear;
  feature: MapboxGeoJSONFeature;
};

@Component({
  selector: 'app-map-popup',
  templateUrl: './map-popup.component.html',
  imports: [
    NgIf,
    AsyncPipe,
    NgClass,
    IconComponent,
    NgSwitch,
    LevelsPopupListComponent,
    GroupedPopupListComponent,
    NgSwitchCase,
    SortPropertiesPipe,
    SchoolsPopupListComponent,
    OriPopupListComponent,
    MatTooltip,
    IconButtonDirective,
  ],
  styleUrls: ['./map-popup.component.scss', '../../../../app.component.scss'],
})
export class MapPopupComponent implements OnInit {
  @Input() data!: PopupData;

  public title!: string;

  constructor(
    private selectedCellService: SelectedCellService,
    public accessService: UserAccessService,
    private http: MapHttpService,
    public popupService: PopupService,
    public layerStore: LayerStoreService,
    private urlService: MapUrlService,
    private modalService: ModalService,
    private router: Router,
    private gaService: GoogleAnalyticsService,
    public colorScaleService: ColorscaleService
  ) {}

  ngOnInit(): void {
    if (this.data.type !== HOVER_POPUP) {
      this.popupService.handlePopupTitle();
      const geoId = this.selectedCellService.getSelectedCellGeoId()!;
      this.loadWrittenInStateFeatures(geoId);
      this.loadAreaScore(geoId);
    }
  }

  public handleClose(): void {
    this.popupService.handleClose();
  }

  public toggleSeeMore(): void {
    this.popupService.toggleSeeMore();
  }

  public handleDownloadXlsx(): void {
    this.popupService.downloadXlsx();
  }

  /**
   * Primary CTA. Everything the popup shows behind a padlock, plus the levels
   * and layers the plan gates, is what this sells - so it opens the same
   * subscription chooser the map's own paywall does rather than a
   * feature-specific upsell.
   */
  public handleGetFullAccess(): void {
    this.modalService.openSubscriptionsModal('all_packages');
  }

  /**
   * Orders a report on this exact tile. The service already knows the
   * selected cell, so the flow opens on its review step with the location
   * and report type filled in.
   */
  public handleOrderReport(): void {
    this.popupService.handleRequestReport();
  }

  // Reuses the same title already shown at the top of this popup (a
  // reverse-geocoded place name, e.g. "33139, Miami Beach, FL") as the
  // address the sales-forecast page's search bar runs on load — see
  // AddressInputComponent.prefillAndSearch(). Falls back to the feature's
  // raw name if the async reverse-geocode hasn't resolved yet.
  public goToSalesForecast(): void {
    const address =
      this.popupService.popupTitle ||
      (this.data.feature.properties?.['name'] as string | undefined);

    this.router.navigate([`/${salesForecastPath('my-business')}`], {
      queryParams: address ? { address } : {},
    });
  }

  /**
   * A locked metric was clicked in the grouped list. Routed through the same
   * modal and the same GA events as the padlock in the right-hand layers menu,
   * so the two upsell surfaces stay comparable in reporting.
   */
  public handleUnlockRequest(request: {
    feature: string;
    label: string;
  }): void {
    if (this.accessService.getIsUnauthorized()) {
      this.gaService.logLockedParamClickGuest();
    } else {
      this.gaService.logLockedParamClickRegistered();
    }

    openLockedFeaturePrompt(
      request.feature,
      request.label,
      this.accessService,
      this.modalService
    );
  }

  /**
   * A readable metric in the popup was clicked: paint the map with it.
   *
   * The same single call the right menu's radio makes - LayersDataService is
   * subscribed to `activeLayer` and does the repaint, and the menu's own radio
   * is bound to the store, so it follows along. The popup stays open: its
   * pinned row is bound to `activeLayer` too, so the row that was clicked
   * becomes the one at the top of the card.
   */
  public handleColorByRequest(request: { feature: string }): void {
    this.layerStore.handleLayerChange(request.feature);
  }

  public handleShare(): void {
    this.urlService.copyUrlToClipboard();
  }

  public handleTooltipModalOpen(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }

  private loadWrittenInStateFeatures(id: string) {
    this.http
      .getDataForSingleCell(
        id,
        this.selectedCellService.getCellType()!,
        // Add land area fraction to features list
        [...featuresToCompareToNationalPopup, LAND_AREA_FRACTION]
      )
      .subscribe((data) => {
        this.data.properties = { ...this.data.properties, ...data.object };
      });
  }

  /**
   * Fetches the four HWW indices for the score block at the top of the popup.
   *
   * They are not part of the request above because they come back nested by
   * scope and have to be flattened (see `spreadHwwScopes`). They are a
   * *separate* request for a blunter reason: this is the only feature name in
   * the popup that no curated list on the client vouches for, so a rejection
   * must not take the other forty metrics down with it. On failure the score
   * block falls back to whatever `feature.state` holds, which is exactly the
   * old behaviour.
   */
  private loadAreaScore(id: string) {
    this.http
      .getDataForSingleCell(
        id,
        this.selectedCellService.getCellType()!,
        HWW_REQUEST_FEATURES
      )
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        if (!data?.object) return;

        this.data.properties = {
          ...this.data.properties,
          ...spreadHwwScopes(data.object),
        };
      });
  }

  protected readonly ORI_POPUP = ORI_POPUP;
  protected readonly SCHOOLS_POPUP = SCHOOLS_POPUP;
  protected readonly DEFAULT_POPUP = DEFAULT_POPUP;
  protected readonly HOVER_POPUP = HOVER_POPUP;
  protected readonly H3_RES5_LEVEL_LAYER = H3_RES5_LEVEL_LAYER;
  protected readonly STATE_LEVEL_LAYER = STATE_LEVEL_LAYER;
}
